import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LimitReachedModal from "@/components/LimitReachedModal";
import FeedbackDialog from "@/components/FeedbackDialog";
import { installLimitInterceptor } from "@/lib/limitInterceptor";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import LibraryPage from "@/pages/LibraryPage";
import AccountPage from "@/pages/AccountPage";
import DownloadsPage from "@/pages/DownloadsPage";
import AIToolsPage from "@/pages/AIToolsPage";
import PlagiarismCheckerPage from "@/pages/tools/PlagiarismCheckerPage";
import CaptionCraftPage from "@/pages/tools/CaptionCraftPage";
import SummarizerPage from "@/pages/tools/SummarizerPage";
import CitationGeneratorPage from "@/pages/tools/CitationGeneratorPage";
import ResumeCheckerPage from "@/pages/tools/ResumeCheckerPage";
import CoverLetterPage from "@/pages/tools/CoverLetterPage";

// Owner Panel
import OwnerLogin from "@/owner/OwnerLogin";
import OwnerLayout from "@/owner/OwnerLayout";
import { OwnerDashboard, OwnerUsers, OwnerUserDetail, OwnerTeams, OwnerAICosts, OwnerBilling, OwnerAnalytics, OwnerAbuse, OwnerSupport, OwnerAlerts, OwnerFeatures, OwnerAudit, OwnerSystem, OwnerSettings } from "@/owner/OwnerPages";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--surface-cream)" }}>
        <div className="text-center animate-fadeIn">
          <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
          <div className="mt-4 w-8 h-8 border-3 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: "var(--rolex-green)", borderTopColor: "transparent" }} />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback - detect session_id in hash SYNCHRONOUSLY
  if (location.pathname === "/auth/callback" || location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/downloads" element={<DownloadsPage />} />
      <Route path="/ai-tools" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
      <Route path="/ai-tools/plagiarism-checker" element={<ProtectedRoute><PlagiarismCheckerPage /></ProtectedRoute>} />
      <Route path="/ai-tools/caption-craft" element={<ProtectedRoute><CaptionCraftPage /></ProtectedRoute>} />
      <Route path="/ai-tools/summarizer" element={<ProtectedRoute><SummarizerPage /></ProtectedRoute>} />
      <Route path="/ai-tools/citation-generator" element={<ProtectedRoute><CitationGeneratorPage /></ProtectedRoute>} />
      <Route path="/ai-tools/resume-checker" element={<ProtectedRoute><ResumeCheckerPage /></ProtectedRoute>} />
      <Route path="/ai-tools/cover-letter" element={<ProtectedRoute><CoverLetterPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
      <Route path="/app/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

      {/* ─── Buddy Owner Panel ─── */}
      <Route path="/owner/login" element={<OwnerLogin />} />
      <Route path="/owner" element={<OwnerLayout />}>
        <Route index element={<Navigate to="/owner/dashboard" replace />} />
        <Route path="dashboard" element={<OwnerDashboard />} />
        <Route path="users" element={<OwnerUsers />} />
        <Route path="users/:id" element={<OwnerUserDetail />} />
        <Route path="teams" element={<OwnerTeams />} />
        <Route path="ai-costs" element={<OwnerAICosts />} />
        <Route path="billing" element={<OwnerBilling />} />
        <Route path="analytics" element={<OwnerAnalytics />} />
        <Route path="abuse" element={<OwnerAbuse />} />
        <Route path="support" element={<OwnerSupport />} />
        <Route path="alerts" element={<OwnerAlerts />} />
        <Route path="features" element={<OwnerFeatures />} />
        <Route path="audit" element={<OwnerAudit />} />
        <Route path="system" element={<OwnerSystem />} />
        <Route path="settings" element={<OwnerSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Global 402 limit-reached interceptor (installed once)
  installLimitInterceptor();
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <LimitReachedModal />
        <FeedbackDialog />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
